/* eslint-disable react/jsx-no-undef */
import { TextField, Typography, useTheme } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcContainer, MprcFileSelection, MprcFormSubHeader, MprcLink, MprcRequestIndicator } from "components";
import { AsyncUploader } from "components/AsyncUploader";
import { MprcImageSelectionDialog } from "components/MprcImageHandler";
import { onImageSelectionUploadError } from "components/MprcImageHandler/MprcImageSelectionDialog";
import { AsyncUploadResult } from "generated/CoreApi_v1";
import MediaUtils from "lib/MediaUtils";
import { useSnackbar } from "notistack";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuestionContext } from "views/assessment/questionBank/Home/EditQuestion/EditQuestion";

/////////////////////////////////////////////////////////////////////////////////

export const EditQuestionComplement: React.FC = () => {

  const { viewData, readonly, handleUpdateRecord } = useQuestionContext();

  const { t } = useTranslation("assessment");
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const [uploading, setUploading] = useState<boolean>(false);
  const [dialog, setDialog] = useState<any>();

  let thumbnailUrl = viewData.thumbnailUrl;
  if (!!thumbnailUrl && viewData?.attach?.mimetype) {
    if (MediaUtils.isVideo(viewData.attach.mimetype)) {
      thumbnailUrl = viewData.attach.mimetype?.endsWith("mp4") ? AppImages.VideoMp4Thumbnail : AppImages.VideoProcessingThumbnail;
    } else if (MediaUtils.isAudio(viewData.attach.mimetype)) {
      thumbnailUrl = AppImages.AudioThumbnail;
    }
  }

  ///
  const handleUpdateAttach = (uploadResult: AsyncUploadResult) => {
    const MAX_DURATION = 180; //3 minutos
    if ((uploadResult.mainFile.mimetype.startsWith("audio") || uploadResult.mainFile.mimetype.startsWith("video")) && (uploadResult.mainFile.metadata.duration || 0) > MAX_DURATION) {
      enqueueSnackbar(t("Question.Edit.VideoDurationLimitMessage"), { variant: "error" });
      return;
    }

    handleUpdateRecord("attach", uploadResult.mainFile);
  };

  ///
  const imageSelectionHandler = useCallback(() => {
    setDialog(
      <MprcImageSelectionDialog
        acceptedFiles={MediaUtils.DropzoneAcceptImages()}
        initialSource="library"
        backgroundType="solid"
        allowEdit={false}
        onClose={
          (image) => {
            setDialog(null);
            if (image != null) {
              const asyncUploader = new
                AsyncUploader({
                  onFileStarted: () => setDialog(<MprcRequestIndicator type="waiting" />),
                  onFileFinished: () => setDialog(null),
                  onFileUploadSuccess: (data: AsyncUploadResult) => {
                    handleUpdateRecord("attach", data.mainFile);
                    setDialog(null);
                  },
                  onFileUploadError: (inst, error) => {
                    onImageSelectionUploadError(inst, error, t);
                    setDialog(null);
                  }
                });

              asyncUploader.uploadImage(image.editedImage ?? image.source);
            }
          }
        }
      />
    );
  }, [viewData]);

  ////////////////////////////////////////////////////////////////////////////
  /// RENDER
  ////////////////////////////////////////////////////////////////////////////

  return (
    <>
      <MprcRequestIndicator type="waiting" visible={uploading} />

      {dialog}

      <MprcContainer>
        <MprcFormSubHeader
          title={t("Question.Edit.Complement.RemarksLabel")}
        />
        <TextField
          fullWidth
          name="remarks"
          autoComplete="off"
          margin="dense"
          value={viewData.remarks || ""}
          onChange={(e) => handleUpdateRecord("remarks", e.target.value)}
          disabled={readonly}
          multiline
          maxRows={3}
          helperText={t("Question.Edit.Complement.RemarksTooltip")}
        />
      </MprcContainer>
      <MprcContainer >
        <MprcFormSubHeader
          title={t("Question.Edit.AttachTitle")}
          subtitle={t("Question.Edit.AttachSubtitle")}
        />
        <MprcFileSelection
          mediaPreview
          selectedFile={viewData.attach}
          thumbnailUrl={thumbnailUrl}
          disabled={readonly}
          onFileUploadStarted={() => {
            setUploading(true);
            handleUpdateRecord("attach", undefined);
          }}
          onFileUploadError={() => {
            handleUpdateRecord("attach", null);
          }}
          onFileUploadFinished={() => setUploading(false)}
          onFileUploadSuccess={handleUpdateAttach}
          onClear={() => handleUpdateRecord("attach", null)}
        />
        {
          viewData.attach?.fileId == null && !readonly &&
          <Typography variant="h5" color={theme.palette.primary.main} marginTop={1}>
            <MprcLink onClick={() => imageSelectionHandler()} color={theme.palette.primary.main}>{t("Question.Edit.SelectImageText")}</MprcLink>
          </Typography>
        }
      </MprcContainer>
    </>
  );
};
